<template>
    <div class="economy-learning-compare">
        <div class="row">
            <div class="col-lg-6">

                <div class="placeholder">
                    <div class="placeholder__heading">
                        <d-tooltip placement="left" identifier="oekonomi.ebitda" />
                        Kontoplan
                    </div>
                    <div class="placeholder padding">

                        <div class="journal-toggle-wrap">
                            <label class="journal-toggle">
                                <span class="journal-toggle__switch" :class="{ 'is-on': includeJournal }">
                                    <input type="checkbox" v-model="includeJournal" />
                                    <span class="journal-toggle__slider"></span>
                                </span>
                                <span class="journal-toggle__label">Medtag kassekladder</span>
                            </label>
                        </div>

                        <div class="warning" v-if="accountingYearsCheck && 'last_year_closed' in accountingYearsCheck && accountingYearsCheck.last_year_closed === false && !$auth.user().name.includes('Demo')">
                            <b>OBS! Seneste regnskabsår ({{ accountingYearsCheck.year }}) er ikke lukket i e-conomic.</b>
                            Dette betyder, at tallene i din kontoplan summeres for både seneste regnskabsår samt det nuværende. Log in på e-conomic og luk det seneste regnskabsår for at se de korrekte tal. OBS! Du kan lukke og åbne et regnskabsår lige så ofte, det er nødvendigt.
                        </div>
                        
                        <div class="ebitda-wrapper" v-if="sortedAccounts">
                            <div class="line top">
                                <div class="name">Konto</div>
                                <div class="balance">Bogført</div>
                                <div class="balance" v-if="includeJournal">Kassekladde</div>
                                <div class="balance" v-if="includeJournal">Total</div>
                            </div>

                            <div
                                class="line"
                                v-for="accountCategory in sortedAccounts"
                                :key="accountCategory.uuid + '_' + accountCategory.name"
                                @click="selectAccountCategory(accountCategory)"
                                :class="{'bold': accountCategory.account_type === 'custom', 'selected': selectedAccountCategory && selectedAccountCategory.uuid === accountCategory.uuid, 'no-hover': ['Dækningsbidrag 1', 'Dækningsbidrag 2', 'EBITDA'].includes(accountCategory.name)}"
                            >
                                <div class="name">
                                    {{ accountCategory.name }}
                                </div>
                                <div class="balance">
                                    {{ accountCategory.entries_total | currency }}
                                </div>
                                <div class="balance" v-if="includeJournal">
                                    {{ accountCategory.journal_total | currency }}
                                </div>
                                <div class="balance" v-if="includeJournal">
                                    {{ accountCategory.total | currency }}
                                </div>
                            </div>
                        </div>

                        <box-loading height="350" v-else />

                    </div>
                </div>
            </div>
            <div class="col-lg-6">
                <div class="placeholder" v-if="selectedAccountCategory">
                    <div class="placeholder__heading">
                        {{ selectedAccountCategory.name }}
                    </div>
                    <div class="placeholder padding">
                        <p v-if="includeJournal && categoryHasJournalAccounts" style="font-size:12px;color:#666;text-align:center;margin-top:-15px;margin-bottom:15px;">
                            Konti med posteringer fra kassekladden er markeret med farvet baggrund.
                        </p>
                        <div class="ebitda-wrapper child" v-if="selectedAccountCategory.accounts.length > 0">
                            <div class="line top">
                                <div class="name">Konto</div>
                                <div class="balance">Bogført</div>
                                <div class="balance" v-if="includeJournal">Kassekladde</div>
                                <div class="balance" v-if="includeJournal">Total</div>
                            </div>
                            <div class="line" v-for="account in selectedAccountCategory.accounts" :key="account.account_number" v-if="(includeJournal ? account.total : account.entries_total) !== 0" @click="selectAccount(account)" :class="{'selected': selectedAccount && selectedAccount.uuid === account.uuid, 'is-journal': includeJournal && Number(account.journal_total) !== 0}">
                                <div class="name">
                                    {{ account.account_number }}
                                    -
                                    {{ account.name }}
                                </div>
                                <div class="balance">
                                    {{ account.entries_total | currency }}
                                </div>
                                <div class="balance" v-if="includeJournal">
                                    {{ account.journal_total | currency }}
                                </div>
                                <div class="balance" v-if="includeJournal">
                                    {{ account.total | currency }}
                                </div>
                            </div>
                            <div class="line">
                                <div class="name">
                                    {{ selectedAccountCategory.name }} i alt
                                </div>
                                <div class="balance">
                                    {{ selectedAccountCategory.entries_total | currency }}
                                </div>
                                <div class="balance" v-if="includeJournal">
                                    {{ selectedAccountCategory.journal_total | currency }}
                                </div>
                                <div class="balance" v-if="includeJournal">
                                    {{ selectedAccountCategory.total | currency }}
                                </div>
                            </div>
                        </div>
                        <div v-else>
                            Denne konto har ingen underkonti
                        </div>

                    </div>
                </div>
                <div v-else>
                    Vælg en konto for at se dens underkonti her
                </div>

                <div class="placeholder" v-if="selectedAccount" style="margin-top: 20px;">
                    <div class="placeholder__heading">
                        Posteringer: {{ selectedAccount.account_number }} - {{ selectedAccount.name }}
                    </div>
                    <div class="placeholder padding">
                        <p v-if="includeJournal && entriesHaveJournal" style="font-size:12px;color:#666;text-align:center;margin-top:-15px;margin-bottom:15px;">
                            Posteringer fra kassekladden er markeret med farvet baggrund.
                        </p>
                        <div class="entries-table" v-if="filteredAccountEntries">
                            <div class="entry-line top">
                                <div class="entry-date">Dato</div>
                                <div class="entry-number">Bilagsnr.</div>
                                <div class="entry-text">Tekst</div>
                                <div class="entry-attachment">Bilag</div>
                                <div class="entry-amount">Beløb</div>
                            </div>
                            <div class="entry-line" v-for="entry in filteredAccountEntries" :key="entry._source + '_' + entry.uuid" :class="{'is-journal': entry._source === 'journal'}">
                                <div class="entry-date">{{ entry.date.split('-').reverse().join('/') }}</div>
                                <div class="entry-number">
                                    <a v-if="entry.voucher_number" :href="'https://secure.e-conomic.com/vouchers/' + entry.voucher_number" target="_blank">
                                        {{ entry.voucher_number }}
                                    </a>
                                    <span v-else>{{ entry.entry_number }}</span>
                                </div>
                                <div class="entry-text">{{ entry.text }}</div>
                                <div class="entry-attachment">
                                    <a v-if="entry.hasAttachment" href="#" @click.prevent="openAttachment(entry)">
                                        <span v-if="entry.loadingAttachment">Henter...</span>
                                        <span v-else>Vis</span>
                                    </a>
                                </div>
                                <div class="entry-amount">{{ entry.amount | currency }}</div>
                            </div>
                            <div v-if="filteredAccountEntries.length === 0" style="margin-top: 10px; font-size: 13px;">
                                Ingen posteringer i denne periode
                            </div>
                        </div>
                        <box-loading height="100" v-else />
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<style lang="scss" scoped>
@import "../../../styles/vars.scss";

    .placeholder {
        height: fit-content;
    }

    .journal-toggle-wrap {
        text-align: center;
        margin-top: -10px;
        margin-bottom: 25px;
    }

    .journal-toggle {
        display: inline-flex;
        align-items: center;
        gap: 10px;
        cursor: pointer;
        user-select: none;

        &__label {
            font-size: 13px;
            color: #666;
        }

        &__switch {
            position: relative;
            display: inline-block;
            width: 36px;
            height: 20px;

            input {
                opacity: 0;
                width: 0;
                height: 0;
                margin: 0;
            }
        }

        &__slider {
            position: absolute;
            cursor: pointer;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: #ccc;
            border-radius: 20px;
            transition: 0.2s ease;

            &::before {
                content: "";
                position: absolute;
                height: 14px;
                width: 14px;
                left: 3px;
                bottom: 3px;
                background: #fff;
                border-radius: 50%;
                transition: 0.2s ease;
            }
        }

        &__switch.is-on .journal-toggle__slider {
            background: $color--dark;

            &::before {
                transform: translateX(16px);
            }
        }
    }

    .ebitda-wrapper {
        font-size: $fontsize--sm;

        &.child .line:last-of-type {
            font-weight: $fontweight--bold;
        }

        .line {
            display: flex;
            position: relative;

            &:not(.no-hover):not(.top) {
                cursor: pointer;
            }

            &:not(.no-hover):not(.top):not(.selected):hover {
                background: rgba(0, 0, 0, 0.05);

                &:before {
                    position: absolute;
                    top: -5px;
                    right: -10px;
                    bottom: -5px;
                    left: -10px;
                    content: "";
                    background: transparent;
                    border: 5px solid rgba(0, 0, 0, 0.05);
                    border-right-width: 10px;
                    border-left-width: 10px;
                    border-radius: $borderradius--main;
                }
            }

            &.clickable {
                cursor: pointer;

                &:hover {
                    background: rgba(0, 0, 0, 0.05);
                }
            }

            &.bold {
                font-weight: $fontweight--bold;
            }

            &.selected {
                // border-bottom: 1px dashed $color--dark;
                background: $color--dark;
                color: #fff;
                position: relative;

                &:before {
                    position: absolute;
                    top: -5px;
                    right: -10px;
                    bottom: -5px;
                    left: -10px;
                    content: "";
                    background: transparent;
                    border: 5px solid $color--dark;
                    border-right-width: 10px;
                    border-left-width: 10px;
                    border-radius: $borderradius--main;
                    border-bottom-width: 6px;
                    border-top-width: 6px;
                }
            }

            &.top {
                font-weight: $fontweight--bold;
            }

            &.is-journal:not(.selected) {
                background: rgba(230, 119, 0, 0.15);

                &:hover {
                    background: rgba(230, 119, 0, 0.25);
                }
            }

            &:not(:last-of-type) {
                margin-bottom: $margin--small;
            }

            &:last-of-type {
                font-weight: $fontweight--bold;

                > div:not(.name) {
                    // text-decoration: underline;
                }
            }

            .name {
                flex-grow: 1;
                padding-right: $padding--small;
            }

            .balance, .budget, .diff {
                flex: 0 0 110px;
                text-align: right;
                white-space: nowrap;
            }
        }
    }

    .entries-table {
            font-size: $fontsize--small;

            .entry-line {
                display: flex;
                padding: 4px 6px;

                &:not(:last-of-type) {
                    border-bottom: 1px solid rgba(0, 0, 0, 0.05);
                }

                &.is-journal {
                    background: rgba(230, 119, 0, 0.15);
                }

                &.top {
                    font-weight: $fontweight--bold;
                    border-bottom: 1px solid rgba(0, 0, 0, 0.15);
                    margin-bottom: 2px;
                }

                .entry-date {
                    flex: 0 0 80px;
                }

                .entry-number {
                    flex: 0 0 70px;
                    padding-right: 10px;

                    a {
                        color: $color--dark;
                        text-decoration: underline;
                    }
                }

                .entry-text {
                    flex: 1;
                    padding-right: 10px;
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                }

                .entry-source {
                    flex: 0 0 90px;
                    padding-right: 10px;

                    .source-pill {
                        font-size: 11px;
                        padding: 1px 6px;
                        border-radius: 3px;
                        background: #e8f0e8;
                        color: #2e6b2e;

                        &.journal {
                            background: #fff3cd;
                            color: #8a6d10;
                        }
                    }
                }

                .entry-attachment {
                    flex: 0 0 50px;
                    padding-right: 10px;

                    a {
                        color: $color--dark;
                        text-decoration: underline;
                    }
                }

                .entry-amount {
                    flex: 0 0 100px;
                    text-align: right;
                }
            }
    }

</style>

<script>
import * as economyService from '@/services/economyService'
import api from '@/axios/index'

export default {
    data () {
        return {
            accountingYearsCheck: null,
            accounts: null,
            selectedAccountCategory: null,
            selectedAccount: null,
            accountEntries: null,
            loading: true,
            includeJournal: true
        }
    },
    created () {
        this.getData()
        this.getContentfulData()
    },
    methods: {
        getContentfulData () {
            const contentful = require('contentful')
            const client = contentful.createClient({
            // This is the space ID. A space is like a project folder in Contentful terms
            space: '3h0kg5qa88is',
            // This is the access token for this space. Normally you get both ID and the token in the Contentful web app
            accessToken: 'KL17Lh_FTD4ZN6iksXFClsWCVJ_OEVVUbIt7RUGofwc'
            })
            // This API call will request an entry with the specified ID from the space defined at the top, using a space-specific access token.
            client
            .getEntry('4SzlILGMZwweEFr9myvbxT')
            .then(entry => {
                // console.log(entry)
                // console.log(entry.fields.tekst.content)
            })
            .catch(err => console.log(err))
        },
        getData () {
            this.loading = true
            this.accounts = null
            this.selectedAccountCategory = null
            let baseParams = {
                date_from: this.$store.getters.getStartDate,
                date_to: this.$store.getters.getEndDate
            }
            // Hent begge kilder parallelt og merge på uuid, så hver række viser
            // bogført + kassekladde + total ved siden af hinanden.
            let entriesPromise = new Promise((resolve, reject) => {
                economyService.accountPlan({ ...baseParams, source: 'entries' }, (r) => resolve(r.data), reject)
            })
            let journalPromise = new Promise((resolve, reject) => {
                economyService.accountPlan({ ...baseParams, source: 'journal' }, (r) => resolve(r.data), reject)
            })
            Promise.all([entriesPromise, journalPromise]).then(([entries, journal]) => {
                this.accounts = this.mergeAccountPlans(entries, journal)
                // Default toggle: enabled hvis der findes data i kassekladden
                // for perioden, ellers slået fra. Undgår at vise tomme kolonner.
                this.includeJournal = this.hasJournalData(this.accounts)
                this.loading = false
            }).catch((err) => {
                this.loading = false
                console.log(err)
            })
        },
        // Tjekker rekursivt om noget i den merged accounts-struktur har en
        // journal_total forskellig fra 0 — hvis ikke kan toggle-defaulten slås fra.
        hasJournalData (nodes) {
            for (let node of nodes) {
                if (Array.isArray(node)) {
                    if (this.hasJournalData(node)) return true
                    continue
                }
                if (Number(node.journal_total) !== 0) return true
                if (Array.isArray(node.accounts)) {
                    for (let acc of node.accounts) {
                        if (Number(acc.journal_total) !== 0) return true
                    }
                }
            }
            return false
        },
        // Merger to source-strukturer (entries + journal) til én struktur hvor
        // hver kategori/konto har entries_total, journal_total og total.
        mergeAccountPlans (entries, journal) {
            let journalByUuid = {}
            let collectByUuid = (groups, target) => {
                groups.forEach((node) => {
                    if (Array.isArray(node)) {
                        collectByUuid(node, target)
                    } else {
                        target[node.uuid] = node
                    }
                })
            }
            collectByUuid(journal, journalByUuid)

            let mergeNode = (node) => {
                if (Array.isArray(node)) return node.map(mergeNode)
                let journalNode = journalByUuid[node.uuid]
                let entriesTotal = Number(node.total) || 0
                let journalTotal = journalNode ? (Number(journalNode.total) || 0) : 0
                let merged = {
                    ...node,
                    entries_total: entriesTotal,
                    journal_total: journalTotal,
                    total: entriesTotal + journalTotal
                }
                if (Array.isArray(node.accounts)) {
                    let journalAccountsByUuid = {}
                    if (journalNode && Array.isArray(journalNode.accounts)) {
                        journalNode.accounts.forEach(a => { journalAccountsByUuid[a.uuid] = a })
                    }
                    merged.accounts = node.accounts.map(acc => {
                        let jAcc = journalAccountsByUuid[acc.uuid]
                        let eTot = Number(acc.total) || 0
                        let jTot = jAcc ? (Number(jAcc.total) || 0) : 0
                        return {
                            ...acc,
                            entries_total: eTot,
                            journal_total: jTot,
                            total: eTot + jTot
                        }
                    })
                }
                return merged
            }
            return entries.map(mergeNode)
        },
        selectAccountCategory (accountCategory) {
            this.selectedAccountCategory = accountCategory
            this.selectedAccount = null
            this.accountEntries = null
        },
        checkAttachments () {
            if (!this.accountEntries || this.accountEntries.length === 0) return
            let voucherNumbers = this.accountEntries
                .filter(e => e.voucher_number)
                .map(e => e.voucher_number)
            if (voucherNumbers.length === 0) return
            api.post('voucher/check-attachments', {
                voucher_numbers: voucherNumbers
            }).then((response) => {
                let withAttachments = response.data
                this.accountEntries.forEach(entry => {
                    this.$set(entry, 'hasAttachment', withAttachments.includes(entry.voucher_number))
                })
            }).catch(() => {})
        },
        openAttachment (entry) {
            if (entry.loadingAttachment) return
            this.$set(entry, 'loadingAttachment', true)
            let newWindow = window.open('about:blank', '_blank')
            api.get('voucher/' + entry.voucher_number + '/attachment', {
                responseType: 'blob'
            }).then((response) => {
                let blob = new Blob([response.data], { type: 'application/pdf' })
                let url = window.URL.createObjectURL(blob)
                newWindow.location.href = url
                entry.loadingAttachment = false
            }).catch((err) => {
                entry.loadingAttachment = false
                if (newWindow) newWindow.close()
                if (err.response && err.response.status === 404) {
                    this.$toastr('info', 'Intet bilag fundet for denne postering', 'Info')
                } else {
                    this.$toastr('error', 'Kunne ikke hente bilag', 'Fejl')
                }
            })
        },
        selectAccount (account) {
            this.selectedAccount = account
            this.accountEntries = null
            let baseParams = {
                date_from: this.$store.getters.getStartDate,
                date_to: this.$store.getters.getEndDate
            }
            // Hent posteringer fra begge kilder så brugeren ser alt samlet,
            // markeret med en "Bogført"/"Kassekladde"-pille i tabellen.
            let entriesPromise = new Promise((resolve, reject) => {
                economyService.accountEntries(account.uuid, { ...baseParams, source: 'entries' }, (r) => resolve(r.data.data), reject)
            })
            let journalPromise = new Promise((resolve, reject) => {
                economyService.accountEntries(account.uuid, { ...baseParams, source: 'journal' }, (r) => resolve(r.data.data), reject)
            })
            Promise.all([entriesPromise, journalPromise]).then(([entries, journal]) => {
                let combined = []
                ;(entries || []).forEach(e => combined.push({ ...e, _source: 'entries' }))
                ;(journal || []).forEach(e => combined.push({ ...e, _source: 'journal' }))
                combined.sort((a, b) => (b.date || '').localeCompare(a.date || ''))
                this.accountEntries = combined
                this.checkAttachments()
            }).catch((err) => {
                console.log(err)
            })
        },
        checkAccountingYears () {
            economyService.accountingYearsCheck({}, (response) => {
                this.accountingYearsCheck = response.data
            }, (err) => {
                console.log(err)
            })
        }
    },
    computed: {
        filteredAccountEntries () {
            if (!this.accountEntries) return null
            if (this.includeJournal) return this.accountEntries
            return this.accountEntries.filter(e => e._source !== 'journal')
        },
        categoryHasJournalAccounts () {
            if (!this.selectedAccountCategory || !this.selectedAccountCategory.accounts) return false
            return this.selectedAccountCategory.accounts.some(a => Number(a.journal_total) !== 0)
        },
        entriesHaveJournal () {
            if (!this.filteredAccountEntries) return false
            return this.filteredAccountEntries.some(e => e._source === 'journal')
        },
        sortedAccounts () {
            if (this.accounts) {
                let arr = []
                this.accounts.forEach((account) => {
                    if (!Array.isArray(account)) {
                        arr.push(account)
                    } else {
                        account.forEach((child) => {
                            arr.push(child)
                        })
                    }
                })
                // console.log(arr)
                return arr
            }
        },
        globalParams () {
            let startDate = this.$store.getters.getStartDate
            let endDate = this.$store.getters.getEndDate
            let department = this.$store.getters.getCurrentDepartment.uuid
            return startDate + endDate + department
        }
    },
    watch: {
        globalParams: function () {
            this.getData()
        }
    }
}
</script>



// WEBPACK FOOTER //
// ebitda.vue?6cabfc74